import {
	Box,
	Typography,
	Container,
	Grid,
	useTheme,
	useMediaQuery,
	Chip,
	Divider,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Button,
	CircularProgress,
	Fade
} from '@mui/material';
import { ScrollView, ImageBackground, View } from 'react-native';
import { useState, useEffect, useRef } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import MicIcon from '@mui/icons-material/Mic';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { primaryColor, HOVER_SCALE, FADE_DURATION, CONTENT_FADE_DURATION } from '../../../config/constants';
import { Footer } from '../../../components/enhancedfooter';
import './Journey.css';

export const Journey = () => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
	const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const [articleDialogOpen, setArticleDialogOpen] = useState(false);
	const [showContent, setShowContent] = useState(false);
	const [showScrollIndicator, setShowScrollIndicator] = useState(false);

	// Refs for each section
	const heroRef = useRef<HTMLDivElement>(null);
	const aboutRef = useRef<HTMLDivElement>(null);
	const journeyRef = useRef<HTMLDivElement>(null);
	const awardsRef = useRef<HTMLDivElement>(null);
	const mediaRef = useRef<HTMLDivElement>(null);
	const creativeRef = useRef<HTMLDivElement>(null);
	const legacyRef = useRef<HTMLDivElement>(null);

	// Visibility state for each section
	const [visibleSections, setVisibleSections] = useState({
		hero: true, // Start with hero visible
		about: false,
		journey: false,
		awards: false,
		media: false,
		creative: false,
		legacy: false,
	});

	useEffect(() => {
		// Preload the main journey image
		const img = new Image();
		img.onload = () => {
			// Add extra 500ms delay after image loads for smooth experience
			setTimeout(() => {
				setShowContent(true);
			}, 1000);
		};
		img.onerror = () => {
			// If image fails to load, still show content after a delay
			setTimeout(() => {
				setShowContent(true);
			}, 1000);
		};
		img.src = require('../../../assets/journey_img.jpg');
	}, []);

	// Intersection Observer for section animations
	useEffect(() => {
		if (!showContent) return; // Don't set up observers until content is shown

		const observerOptions = {
			threshold: 0.2, // Trigger when 10% of the element is visible
			rootMargin: '0px 0px -50px 0px', // Start animation 50px before element comes into view
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					const sectionName = entry.target.getAttribute('data-section');
					if (sectionName) {
						setVisibleSections(prev => ({
							...prev,
							[sectionName]: true
						}));
					}
				}
			});
		}, observerOptions);

		// Observe all sections
		const sections = [
			{ ref: heroRef, name: 'hero' },
			{ ref: aboutRef, name: 'about' },
			{ ref: journeyRef, name: 'journey' },
			{ ref: awardsRef, name: 'awards' },
			{ ref: mediaRef, name: 'media' },
			{ ref: creativeRef, name: 'creative' },
			{ ref: legacyRef, name: 'legacy' },
		];

		sections.forEach(({ ref, name }) => {
			if (ref.current) {
				ref.current.setAttribute('data-section', name);
				observer.observe(ref.current);
			}
		});

		return () => observer.disconnect();
	}, [showContent]);

	// Scroll indicator logic
	useEffect(() => {
		if (showContent) {
			// Show scroll indicator 2 seconds after content loads
			const showTimer = setTimeout(() => {
				setShowScrollIndicator(true);
			}, 2000);

			// Hide on scroll
			const handleScroll = () => {
				if (showScrollIndicator) {
					setShowScrollIndicator(false);
				}
			};

			window.addEventListener('scroll', handleScroll, { passive: true });

			return () => {
				clearTimeout(showTimer);
				window.removeEventListener('scroll', handleScroll);
			};
		}

		return () => { }; // Return empty cleanup function if showContent is false
	}, [showContent, showScrollIndicator]);

	// Loading Screen Component
	const LoadingScreen = () => (
		<Box
			sx={{
				position: 'fixed',
				top: 0,
				left: 0,
				width: '100vw',
				height: '100vh',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
				alignItems: 'center',
				backgroundColor: theme.palette.background.default,
				zIndex: 9999
			}}
		>
			<Box sx={{ position: 'relative', mb: 4 }}>
				<MilitaryTechIcon
					className="journey-pulse"
					sx={{
						color: primaryColor,
						fontSize: 48,
					}}
				/>
			</Box>
			<CircularProgress
				size={30}
				sx={{
					color: primaryColor,
					mb: 3
				}}
			/>
			<Typography
				variant="h3"
				sx={{
					color: theme.palette.text.primary,
					fontFamily: 'logoFont',
					letterSpacing: '0.1em',
					mb: 1
				}}
			>
				Loading
			</Typography>
		</Box>
	);

	// Scroll Indicator Component with Shimmer Effect
	const ScrollIndicator = () => (
		<Fade in={showScrollIndicator} timeout={500}>
			<Box
				px={2}
				py={1}
				className="journey-bounce"
				sx={{
					backgroundColor: theme.palette.background.default,
					position: 'fixed',
					bottom: 40,
					right: isSmallScreen ? '17%' : '5%',
					transform: 'translateX(-50%)',
					zIndex: 1000,
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius: 10,
					cursor: 'pointer',
					boxShadow: `0 0 10px 0 rgba(0, 0, 0, 0.2)`,
				}}
				onClick={() => {
					window.scrollTo({ top: window.innerHeight * 0.8, behavior: 'smooth' });
					setShowScrollIndicator(false);
				}}
			>
				<KeyboardArrowDownIcon
					sx={{
						fontSize: 26,
						background: `linear-gradient(45deg, ${primaryColor}, rgba(255,255,255,0.6))`,
						backgroundSize: '200px 100%',
						backgroundClip: 'text',
						WebkitBackgroundClip: 'text',
						color: primaryColor,
						animation: 'shimmer 2s infinite linear',
						filter: `drop-shadow(0 0 8px ${primaryColor}60)`,
					}}
				/>
				<Typography
					sx={{
						letterSpacing: '0.1em',
						fontSize: '0.6rem',
						fontWeight: 600,
						background: `linear-gradient(90deg, ${primaryColor} 0%, rgba(255,255,255,0.8) 50%, ${primaryColor} 100%)`,
						backgroundSize: '200px 100%',
						backgroundClip: 'text',
						WebkitBackgroundClip: 'text',
						color: 'transparent',
						animation: 'shimmer 2s infinite linear',
						textShadow: `0 0 10px ${primaryColor}40`,
						justifyContent: 'center',
					}}
				>
					Scroll to Explore
				</Typography>

			</Box>
		</Fade>
	);

	return (
		<>
			{/* Loading Screen */}
			{!showContent && <LoadingScreen />}

			{/* Scroll Indicator */}
			{showContent && !visibleSections.legacy && ScrollIndicator()}

			<ScrollView style={{ flex: 1 }}>
				{/* Main Content */}
				<Fade in={showContent} timeout={CONTENT_FADE_DURATION}>
					<div>
						{/* Content */}
						<Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
							{/* Full Width Journey Image */}
							<Box
								sx={{
									width: '100vw',
									height: isSmallScreen ? '25vh' : '50vh',
									position: 'relative',
									left: '50%',
									right: '50%',
									marginLeft: '-50vw',
									marginRight: '-50vw',
									mb: isSmallScreen ? 8 : 12
								}}
							>
								<ImageBackground
									source={require("../../../assets/journey_img.jpg")}
									resizeMode="cover"
									style={{
										width: "100%",
										height: "100%",
										justifyContent: "center",
										alignItems: "center"
									}}
								>
									<View style={{
										position: "absolute",
										width: "100%",
										height: "100%",
										backgroundColor: 'rgba(0, 0, 0, 0.4)',
										zIndex: 1
									}} />
								</ImageBackground>
							</Box>

							{/* Hero Title Section */}
							<Fade in={visibleSections.hero} timeout={FADE_DURATION}>
								<Box ref={heroRef} sx={{ textAlign: 'center', mb: isSmallScreen ? 6 : 8 }}>
									{/* Name Typography */}
									<Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
										<Typography
											sx={{
												fontFamily: 'logoFont, Arial, sans-serif',
												fontWeight: 'bold',
												color: primaryColor,
												letterSpacing: '0.02em',
												fontSize: isExtraSmallScreen ? '1.5rem' : isSmallScreen ? '2.2rem' : '3rem',
												transition: 'all 0.5s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}}
										>
											ToeFadeIt
										</Typography>
										{/* TOUFIQUE */}
										<Typography
											sx={{
												fontFamily: 'secondaryFont, Arial, sans-serif',
												fontWeight: 'bold',
												color: theme.palette.text.primary,
												letterSpacing: '0.05em',
												fontSize: isExtraSmallScreen ? '1.8rem' : isSmallScreen ? '2.5rem' : '5rem',
												lineHeight: 0.9,
												textAlign: 'center',
												mb: isSmallScreen ? -0.5 : -0.9, transition: 'all 0.5s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}}
										>
											TOUFIQUE
										</Typography>
										{/* BEHARDIEN */}
										<Typography
											sx={{
												fontFamily: 'secondaryFont, Arial, sans-serif',
												fontWeight: 'bold',
												color: theme.palette.text.primary,
												letterSpacing: '0.05em',
												textAlign: 'center',
												fontSize: isExtraSmallScreen ? '1.8rem' : isSmallScreen ? '2.5rem' : '5rem',
												lineHeight: 0.9,
												mt: isSmallScreen ? -0.5 : -0.8,
												transition: 'all 0.5s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}}
										>
											BEHARDIEN
										</Typography>
										{/* ToeFadeIt - Overlaid in center */}
									</Box>
									<Typography
										variant="h6"
										sx={{
											color: "grey",
											fontWeight: 800,
											mb: 4,
											maxWidth: '600px',
											mx: 'auto',
											textAlign: 'center',
											fontSize: isSmallScreen ? '1rem' : '1.1rem'
										}}
									>
										From Cape Town streets to International Recognition
									</Typography>
								</Box>
							</Fade>

							{/* About Section */}
							<Fade in={visibleSections.about} timeout={FADE_DURATION}>
								<Box ref={aboutRef} sx={{ mb: isSmallScreen ? 8 : 12 }}>
									<Box sx={{ maxWidth: '800px', mx: 'auto', px: isSmallScreen ? 2 : 4 }}>
										<Typography
											variant={isSmallScreen ? 'h6' : 'h5'}
											sx={{
												lineHeight: 1.8,
												color: theme.palette.text.primary,
												fontSize: isExtraSmallScreen ? '1.1rem' : isSmallScreen ? '1.3rem' : '1.5rem',
												fontWeight: 400,
												textAlign: 'center',
												mb: 4
											}}
										>
											Toufique's shop is more than a barbershop—it's a creative community hub.
											Inside, clients are surrounded by street art, beats, conversation, and purpose.
											Whether you're there for a fade, a beard line-up, or a dose of inspiration,
											you're stepping into a space that prioritizes identity, resilience, and reinvention.
										</Typography>
										<Typography
											variant={isSmallScreen ? 'body1' : 'h6'}
											sx={{
												lineHeight: 1.8,
												color: theme.palette.text.secondary,
												fontSize: isExtraSmallScreen ? '1rem' : isSmallScreen ? '1.1rem' : '1.2rem',
												textAlign: 'center',
												fontStyle: 'italic',
											}}
										>
											While he hasn't launched retail grooming products yet, they're in the pipeline—part of
											a broader vision to grow "Toe Fade It" into a lifestyle movement.
										</Typography>
									</Box>
								</Box>
							</Fade>

							{/* Section Divider */}
							<Divider sx={{ mb: isSmallScreen ? 8 : 12, maxWidth: '200px', mx: 'auto', borderBottomWidth: 2 }} />

							{/* Background Story Section */}
							<Fade in={visibleSections.journey} timeout={FADE_DURATION}>
								<Box ref={journeyRef} sx={{ mb: isSmallScreen ? 10 : 14 }}>
									<Box sx={{ maxWidth: '900px', mx: 'auto', px: isSmallScreen ? 2 : 4 }}>
										<Box sx={{ textAlign: 'center', mb: isSmallScreen ? 4 : 6 }}>
											<MilitaryTechIcon sx={{
												color: primaryColor,
												fontSize: 40,
												mb: 3,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}} />
											<Typography
												variant={isSmallScreen ? 'h4' : 'h3'}
												sx={{
													fontFamily: 'logoFont',
													letterSpacing: "0.1em",
													color: theme.palette.text.primary,
													mb: isSmallScreen ? 2 : 4,
													textAlign: 'center',
													transition: 'all 0.3s ease',
													cursor: 'pointer',
													'&:hover': {
														transform: `scale(${HOVER_SCALE})`,
													}
												}}
											>
												The Journey
											</Typography>
										</Box>

										<Grid container spacing={isSmallScreen ? 4 : 6} sx={{ mt: 2 }}>
											<Grid item xs={12} md={6}>
												<Box sx={{ mb: isSmallScreen ? 4 : 0 }}>
													<Typography
														variant="h6"
														sx={{
															fontWeight: 'bold',
															mb: 3,
															color: theme.palette.text.primary,
															fontSize: isSmallScreen ? '1.2rem' : '1.3rem'
														}}
													>
														From Humble Beginnings
													</Typography>
													<Typography
														variant="body1"
														sx={{
															lineHeight: 1.8,
															color: theme.palette.text.secondary,
															fontSize: isSmallScreen ? '1rem' : '1.1rem',
															mb: 3
														}}
													>
														It started at a five-star hotel in Constantia, Cape Town. Cleaning floors,
														serving clients tea, handing out flyers—Toufique did it all. When offered
														the chance to study ladies hairdressing, he grabbed it with both hands.
													</Typography>
													<Typography
														variant="body1"
														sx={{
															lineHeight: 1.8,
															color: theme.palette.text.secondary,
															fontSize: isSmallScreen ? '1rem' : '1.1rem'
														}}
													>
														Financial difficulties cut his formal training short, but the streets of
														Bree and Buitengracht became his real classroom, where he learned that
														consistency beats fame every time.
													</Typography>
												</Box>
											</Grid>

											<Grid item xs={12} md={6}>
												<Box>
													<Typography
														variant="h6"
														sx={{
															fontWeight: 'bold',
															mb: 3,
															color: theme.palette.text.primary,
															fontSize: isSmallScreen ? '1.2rem' : '1.3rem'
														}}
													>
														Philosophy & Craft
													</Typography>
													<Box
														sx={{
															backgroundColor: theme.palette.mode === 'dark'
																? 'rgba(255, 255, 255, 0.03)'
																: 'rgba(0, 0, 0, 0.02)',
															borderLeft: `4px solid ${primaryColor}`,
															p: 3,
															borderRadius: 2,
															mb: 3
														}}
													>
														<Typography
															variant="body1"
															sx={{
																fontStyle: 'italic',
																color: theme.palette.text.primary,
																fontSize: isSmallScreen ? '1rem' : '1.1rem',
																lineHeight: 1.6
															}}
														>
															"If you give me six hours to cut down a tree, I'll spend four hours sharpening my blade."
														</Typography>
													</Box>
													<Typography
														variant="body1"
														sx={{
															lineHeight: 1.8,
															color: theme.palette.text.secondary,
															fontSize: isSmallScreen ? '1rem' : '1.1rem'
														}}
													>
														Drawing since age 10, with graffiti as his lifeline, Toufique's creative mind
														fuels everything he does. His barbershop showcases countless pieces from local
														artists—his contribution to keeping the culture alive.
													</Typography>
												</Box>
											</Grid>
										</Grid>
									</Box>
								</Box>
							</Fade>

							{/* Section Divider */}
							<Divider sx={{ mb: isSmallScreen ? 8 : 12, maxWidth: '200px', mx: 'auto', borderBottomWidth: 2 }} />

							{/* Awards Section */}
							<Fade in={visibleSections.awards} timeout={FADE_DURATION}>
								<Box ref={awardsRef} sx={{ mb: isSmallScreen ? 10 : 14 }}>
									<Box sx={{ textAlign: 'center', mb: isSmallScreen ? 6 : 8 }}>
										<EmojiEventsIcon sx={{
											color: '#FFD700',
											fontSize: 40,
											mb: 2,
											transition: 'all 0.3s ease',
											cursor: 'pointer',
											'&:hover': {
												transform: `scale(${HOVER_SCALE})`,
											}
										}} />
										<Typography
											variant={isSmallScreen ? 'h4' : 'h3'}
											sx={{
												fontFamily: 'logoFont',
												letterSpacing: "0.1em",
												color: theme.palette.text.primary,
												mb: 2,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}}
										>
											Awards
										</Typography>
										<Typography
											variant="body1"
											sx={{
												color: theme.palette.text.secondary,
												maxWidth: '600px',
												mx: 'auto',
												fontSize: isSmallScreen ? '1rem' : '1.1rem'
											}}
										>
											Recognition for excellence in the craft of barbering
										</Typography>
									</Box>

									<Box sx={{ maxWidth: '900px', mx: 'auto' }}>
										<Grid container spacing={isSmallScreen ? 4 : 6}>
											<Grid item xs={12} md={6}>
												<Box sx={{ textAlign: isSmallScreen ? 'center' : 'left', px: isSmallScreen ? 2 : 3 }}>
													<Box sx={{ mb: 3 }}>
														<Chip
															label="1st Place"
															sx={{
																backgroundColor: '#FFD700',
																color: 'black',
																fontWeight: 'bold',
																fontSize: '0.9rem',
																mb: 2
															}}
														/>
														<Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.text.primary, fontSize: '2rem' }}>
															2019
														</Typography>
													</Box>
													<Typography
														variant="h5"
														sx={{
															fontWeight: 'bold',
															mb: 2,
															color: theme.palette.text.primary,
															fontSize: isSmallScreen ? '1.3rem' : '1.5rem'
														}}
													>
														Best of Barbering
													</Typography>
													<Typography
														variant="body1"
														sx={{
															mb: 3,
															color: theme.palette.text.secondary,
															fontSize: isSmallScreen ? '0.95rem' : '1rem',
															lineHeight: 1.6
														}}
													>
														Wahl Professional Salon International Photographic Competition
													</Typography>
													<Typography
														variant="body2"
														sx={{
															lineHeight: 1.7,
															color: theme.palette.text.secondary,
															fontSize: isSmallScreen ? '0.9rem' : '0.95rem'
														}}
													>
														Toufique's entry stood out in a highly competitive field, highlighting
														the finesse and depth of his clipper work.
													</Typography>
												</Box>
											</Grid>

											<Grid item xs={12} md={6}>
												<Box sx={{ textAlign: isSmallScreen ? 'center' : 'left', px: isSmallScreen ? 2 : 3 }}>
													<Typography
														variant="h5"
														sx={{
															fontWeight: 'bold',
															mb: 3,
															color: theme.palette.text.primary,
															fontSize: isSmallScreen ? '1.3rem' : '1.5rem'
														}}
													>
														Industry Recognition
													</Typography>
													<Typography
														variant="body1"
														sx={{
															lineHeight: 1.7,
															color: theme.palette.text.primary,
															mb: 3,
															fontSize: isSmallScreen ? '1rem' : '1.1rem'
														}}
													>
														His win came at a time when South African barbers were making international waves.
													</Typography>
													<Typography
														variant="body2"
														sx={{
															lineHeight: 1.7,
															color: theme.palette.text.secondary,
															fontStyle: 'italic',
															fontSize: isSmallScreen ? '0.9rem' : '0.95rem'
														}}
													>
														Judges called the clipper work from SA "unbelievable," placing Toufique
														among the leaders of this rising movement.
													</Typography>
												</Box>
											</Grid>
										</Grid>
									</Box>
								</Box>
							</Fade>

							{/* Section Divider */}
							<Divider sx={{ mb: isSmallScreen ? 8 : 12, maxWidth: '200px', mx: 'auto', borderBottomWidth: 2 }} />

							{/* Media Recognition Section */}
							<Fade in={visibleSections.media} timeout={FADE_DURATION}>
								<Box ref={mediaRef} sx={{ mb: isSmallScreen ? 10 : 14 }}>
									<Box sx={{ textAlign: 'center', mb: isSmallScreen ? 4 : 6 }}>
										{/* GQ Logo */}
										<Box sx={{ mb: 3 }}>
											<Box
												sx={{
													display: 'inline-flex',
													alignItems: 'center',
													justifyContent: 'center',
													backgroundColor: '#000000',
													borderRadius: 2,
													px: 3,
													py: 1.5,
													mb: 2,
													transition: 'all 0.3s ease',
													cursor: 'pointer',
													boxShadow: theme.palette.mode === 'dark'
														? '0 4px 12px rgba(255, 255, 255, 0.1)'
														: '0 4px 12px rgba(0, 0, 0, 0.15)',
													'&:hover': {
														transform: `scale(${HOVER_SCALE})`,
														boxShadow: theme.palette.mode === 'dark'
															? '0 8px 24px rgba(255, 255, 255, 0.2)'
															: '0 8px 24px rgba(0, 0, 0, 0.25)',
													}
												}}
											>
												<img
													src="https://www.gq.com/verso/static/gq-global/assets/logo.svg"
													alt="GQ Logo"
													style={{
														height: isSmallScreen ? '24px' : '30px',
														filter: 'brightness(0) invert(1)'  // Makes SVG white
													}}
												/>
											</Box>
											<Typography
												variant="body2"
												sx={{
													color: theme.palette.text.secondary,
													fontSize: '0.85rem',
													fontWeight: 500,
													textTransform: 'uppercase',
													letterSpacing: '0.1em'
												}}
											>
												SOUTH AFRICA
											</Typography>
										</Box>

										<Typography
											variant={isSmallScreen ? 'h4' : 'h3'}
											sx={{
												fontWeight: 'bold',
												fontFamily: 'logoFont',
												letterSpacing: "0.1em",
												color: theme.palette.text.primary,
												mb: 3,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}}
										>
											Featured Article
										</Typography>
										<Typography
											variant="body1"
											sx={{
												color: theme.palette.text.secondary,
												fontSize: isSmallScreen ? '1rem' : '1.1rem',
												maxWidth: '700px',
												mx: 'auto',
												lineHeight: 1.7,
												fontStyle: 'italic'
											}}
										>
											"Toufique Behardien is a barber worth knowing" - GQ South Africa recognized
											his unconventional barbershop and unique approach to combining art, culture, and craft.
										</Typography>
									</Box>

									<Box sx={{ maxWidth: '800px', mx: 'auto', textAlign: 'center' }}>
										<Box
											sx={{
												backgroundColor: theme.palette.mode === 'dark'
													? 'rgba(255, 255, 255, 0.05)'
													: 'rgba(0, 0, 0, 0.02)',
												borderRadius: 3,
												p: isSmallScreen ? 3 : 4,
												border: `1px solid ${theme.palette.divider}`,
												mb: 4
											}}
										>
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, gap: 2 }}>
												<Box
													sx={{
														backgroundColor: '#000000',
														borderRadius: 1,
														px: 1.5,
														py: 0.5,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														transition: 'all 0.3s ease',
														cursor: 'pointer',
														'&:hover': {
															transform: `scale(${HOVER_SCALE})`,
														}
													}}
												>
													<img
														src="https://www.gq.com/verso/static/gq-global/assets/logo.svg"
														alt="GQ Logo"
														style={{
															height: '16px',
															filter: 'brightness(0) invert(1)'  // Makes SVG white
														}}
													/>
												</Box>
												<Typography
													variant="h6"
													sx={{
														fontWeight: 'bold',
														color: theme.palette.text.primary,
														fontSize: isSmallScreen ? '1rem' : '1.1rem'
													}}
												>
													South Africa - October 2022
												</Typography>
											</Box>
											<Typography
												variant="body1"
												sx={{
													color: theme.palette.text.secondary,
													lineHeight: 1.8,
													fontSize: isSmallScreen ? '1rem' : '1.1rem',
													fontStyle: 'italic'
												}}
											>
												"When a client walks in for the first time, they generally fall in love with my space
												and how it's curated... I try to provide them a brand-new experience that makes them feel special."
											</Typography>
											<Typography
												variant="body2"
												sx={{
													mt: 2,
													mb: 3,
													color: theme.palette.text.secondary,
													fontSize: '0.9rem'
												}}
											>
												— Toufique Behardien, GQ Interview
											</Typography>

											<Box sx={{ display: 'flex', justifyContent: 'center' }}>
												<Button
													variant="contained"
													onClick={() => setArticleDialogOpen(true)}
													sx={{
														backgroundColor: primaryColor,
														color: 'white',
														borderRadius: 8,
														px: 3,
														py: 1,
														fontSize: '0.9rem',
														fontWeight: 'bold',
														textTransform: 'none',
														transition: 'all 0.3s ease',
														boxShadow: theme.palette.mode === 'dark'
															? '0 4px 12px rgba(255, 255, 255, 0.1)'
															: '0 4px 12px rgba(0, 0, 0, 0.15)',
														'&:hover': {
															backgroundColor: primaryColor,
															transform: `translateY(-2px) scale(1.05)`,
															boxShadow: theme.palette.mode === 'dark'
																? '0 6px 20px rgba(255, 255, 255, 0.15)'
																: '0 6px 20px rgba(0, 0, 0, 0.25)',
														}
													}}
												>
													Check out the article here!
												</Button>
											</Box>
										</Box>
									</Box>
								</Box>
							</Fade>

							{/* Section Divider */}
							<Divider sx={{ mb: isSmallScreen ? 8 : 12, maxWidth: '200px', mx: 'auto', borderBottomWidth: 2 }} />

							{/* Creative Work Section */}
							<Fade in={visibleSections.creative} timeout={FADE_DURATION}>
								<Box ref={creativeRef} sx={{ mb: isSmallScreen ? 10 : 14 }}>
									<Box sx={{ textAlign: 'center', mb: isSmallScreen ? 6 : 8 }}>
										{/* Creative Icons */}
										<Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: isSmallScreen ? 1 : 2 }}>
											<Box
												sx={{
													position: 'relative',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: isSmallScreen ? 45 : 50,
													height: isSmallScreen ? 45 : 50,
													borderRadius: '50%',
													background: `linear-gradient(135deg, ${primaryColor}20, ${primaryColor}40)`,
													border: `3px solid ${primaryColor}60`,
													transition: 'all 0.3s ease',
													'&:hover': {
														transform: `translateY(-5px) rotate(10deg) scale(${HOVER_SCALE})`,
														boxShadow: `0 10px 30px ${primaryColor}30`
													}
												}}
											>
												<ContentCutIcon
													sx={{
														fontSize: 20,
														color: primaryColor,
														transform: 'rotate(-15deg)'
													}}
												/>
											</Box>

											<Box
												sx={{
													position: 'relative',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: isSmallScreen ? 45 : 50,
													height: isSmallScreen ? 45 : 50,
													borderRadius: '50%',
													background: `linear-gradient(135deg, #FF6B6B20, #FF6B6B40)`,
													border: '3px solid #FF6B6B60',
													transition: 'all 0.3s ease',
													'&:hover': {
														transform: `translateY(-5px) scale(${HOVER_SCALE})`,
														boxShadow: '0 10px 30px #FF6B6B30'
													}
												}}
											>
												<MicIcon
													sx={{
														fontSize: 20,
														color: '#FF6B6B'
													}}
												/>
											</Box>

											<Box
												sx={{
													position: 'relative',
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'center',
													width: isSmallScreen ? 45 : 50,
													height: isSmallScreen ? 45 : 50,
													borderRadius: '50%',
													background: `linear-gradient(135deg, #4ECDC420, #4ECDC440)`,
													border: '3px solid #4ECDC460',
													transition: 'all 0.3s ease',
													'&:hover': {
														transform: `translateY(-5px) rotate(-10deg) scale(${HOVER_SCALE})`,
														boxShadow: '0 10px 30px #4ECDC430'
													}
												}}
											>
												<PaletteIcon
													sx={{
														fontSize: 20,
														color: '#4ECDC4'
													}}
												/>
											</Box>
										</Box>
										<Typography
											variant={isSmallScreen ? 'h4' : 'h3'}
											sx={{
												fontWeight: 'bold',
												fontFamily: 'logoFont',
												letterSpacing: "0.1em",
												color: theme.palette.text.primary,
												mb: 3,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												'&:hover': {
													transform: `scale(${HOVER_SCALE})`,
												}
											}}
										>
											Creative Work
										</Typography>

										<Typography
											variant="body1"
											sx={{
												color: theme.palette.text.secondary,
												fontSize: isSmallScreen ? '1rem' : '1.1rem',
												maxWidth: '700px',
												mx: 'auto',
												mt: 6,
												lineHeight: 1.7
											}}
										>
											"Toe Fade It" encompasses a barbershop, recording studio, and street art gallery—a
											complete creative ecosystem. Beyond cutting hair, Toufique produces music videos
											and curates art from local graffiti artists, creating a space where culture thrives.
										</Typography>
									</Box>

									<Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
										<Grid container spacing={isSmallScreen ? 4 : 6}>
											<Grid item xs={12} md={6}>
												<Box sx={{ mb: isSmallScreen ? 4 : 0 }}>
													<Box
														sx={{
															borderRadius: 3,
															overflow: 'hidden',
															boxShadow: theme.palette.mode === 'dark'
																? '0 8px 24px rgba(0, 0, 0, 0.3)'
																: '0 8px 24px rgba(0, 0, 0, 0.1)',
															transition: 'all 0.3s ease',
															'&:hover': {
																transform: 'translateY(-4px)',
																boxShadow: theme.palette.mode === 'dark'
																	? '0 12px 40px rgba(0, 0, 0, 0.4)'
																	: '0 12px 40px rgba(0, 0, 0, 0.15)',
															}
														}}
													>
														<Box
															sx={{
																position: 'relative',
																paddingBottom: '56.25%', // 16:9 aspect ratio
																height: 0,
																overflow: 'hidden'
															}}
														>
															<iframe
																src="https://www.youtube.com/embed/eLCPqBQxOvQ"
																title="Music Video Project I"
																style={{
																	position: 'absolute',
																	top: 0,
																	left: 0,
																	width: '100%',
																	height: '100%',
																	border: 0
																}}
																allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
																allowFullScreen
															/>
														</Box>
													</Box>
													<Box sx={{ mt: 3, textAlign: isSmallScreen ? 'center' : 'left' }}>
														<Typography
															variant="h6"
															sx={{
																fontWeight: 'bold',
																mb: 2,
																color: theme.palette.text.primary,
																fontSize: isSmallScreen ? '1.2rem' : '1.3rem'
															}}
														>
															Klein Duiwel - Kroon
														</Typography>
														<Typography
															variant="body2"
															sx={{
																color: theme.palette.text.secondary,
																lineHeight: 1.7,
																fontSize: isSmallScreen ? '0.9rem' : '1rem'
															}}
														>
															A raw, authentic visual narrative that captures the essence of Cape Town's underground
															music scene. Toufique's directorial vision weaves together street culture and artistic
															expression, creating a compelling story that resonates with Klein Duiwel's powerful sound.
															Every frame reflects the gritty beauty and creative energy of the community that shaped both artists.
														</Typography>
													</Box>
												</Box>
											</Grid>

											<Grid item xs={12} md={6}>
												<Box sx={{ mb: isSmallScreen ? 4 : 0 }}>
													<Box
														sx={{
															borderRadius: 3,
															overflow: 'hidden',
															boxShadow: theme.palette.mode === 'dark'
																? '0 8px 24px rgba(0, 0, 0, 0.3)'
																: '0 8px 24px rgba(0, 0, 0, 0.1)',
															transition: 'all 0.3s ease',
															'&:hover': {
																transform: 'translateY(-4px)',
																boxShadow: theme.palette.mode === 'dark'
																	? '0 12px 40px rgba(0, 0, 0, 0.4)'
																	: '0 12px 40px rgba(0, 0, 0, 0.15)',
															}
														}}
													>
														<Box
															sx={{
																position: 'relative',
																paddingBottom: '56.25%', // 16:9 aspect ratio
																height: 0,
																overflow: 'hidden'
															}}
														>
															<iframe
																src="https://www.youtube.com/embed/GjIWB-tqJYg"
																title="Music Video Project II"
																style={{
																	position: 'absolute',
																	top: 0,
																	left: 0,
																	width: '100%',
																	height: '100%',
																	border: 0
																}}
																allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
																allowFullScreen
															/>
														</Box>
													</Box>
													<Box sx={{ mt: 3, textAlign: isSmallScreen ? 'center' : 'left' }}>
														<Typography
															variant="h6"
															sx={{
																fontWeight: 'bold',
																mb: 2,
																color: theme.palette.text.primary,
																fontSize: isSmallScreen ? '1.2rem' : '1.3rem'
															}}
														>
															Klein Duiwel - Doeit Jas
														</Typography>
														<Typography
															variant="body2"
															sx={{
																color: theme.palette.text.secondary,
																lineHeight: 1.7,
																fontSize: isSmallScreen ? '0.9rem' : '1rem'
															}}
														>
															Produced and directed by Toufique, this music video showcases technical precision
															combined with creative storytelling. From location scouting in Cape Town's iconic
															neighborhoods to post-production color grading, every element was carefully crafted
															to amplify Klein Duiwel's musical vision and bring their artistic concept to life.
														</Typography>
													</Box>
												</Box>
											</Grid>
										</Grid>
									</Box>
								</Box>
							</Fade>

							{/* Section Divider */}
							<Divider sx={{ mb: isSmallScreen ? 8 : 12, maxWidth: '200px', mx: 'auto', borderBottomWidth: 2 }} />

							{/* Legacy Section */}
							<Fade in={visibleSections.legacy} timeout={FADE_DURATION}>
								<Box ref={legacyRef} sx={{ textAlign: 'center', mb: isSmallScreen ? 2 : 4 }}>
									<WhatshotIcon sx={{
										color: primaryColor,
										fontSize: 40,
										mb: 2,
										transition: 'all 0.3s ease',
										cursor: 'pointer',
										'&:hover': {
											transform: `scale(${HOVER_SCALE})`,
										}
									}} />
									<Typography
										variant={isSmallScreen ? 'h3' : 'h2'}
										sx={{
											fontWeight: 'bold',
											fontFamily: 'logoFont',
											letterSpacing: "0.1em",
											color: theme.palette.text.primary,
											mb: 2,
											transition: 'all 0.3s ease',
											cursor: 'pointer',
											'&:hover': {
												transform: `scale(${HOVER_SCALE})`,
											}
										}}
									>
										Legacy in the Making
									</Typography>

									<Typography
										variant={isSmallScreen ? 'h5' : 'h4'}
										sx={{
											fontWeight: 'bold',
											mb: isSmallScreen ? 4 : 6,
											color: primaryColor,
											letterSpacing: "0.1em",
											fontSize: isSmallScreen ? '1.5rem' : '2rem',
											fontFamily: 'logoFont',
											transition: 'all 0.3s ease',
											cursor: 'pointer',
											'&:hover': {
												transform: `scale(${HOVER_SCALE})`,
											}
										}}
									>
										Toufique Behardien
									</Typography>

									<Typography
										variant={isSmallScreen ? 'h6' : 'h5'}
										sx={{
											lineHeight: 1.8,
											color: theme.palette.text.primary,
											maxWidth: '800px',
											mx: 'auto',
											fontSize: isExtraSmallScreen ? '1.1rem' : isSmallScreen ? '1.3rem' : '1.5rem',
											fontWeight: 400,
											px: isSmallScreen ? 2 : 0,
											mb: 4
										}}
									>
										More than a barber—he's a culture-shaper, a community builder, and a storyteller
										with clippers in hand. His work reflects the hustle, heart, and healing of Cape Town's
										streets, and his brand continues to inspire barbers, artists, and dreamers across
										South Africa and beyond.
									</Typography>

									<Box
										sx={{
											maxWidth: '700px',
											mx: 'auto',
											backgroundColor: theme.palette.mode === 'dark'
												? 'rgba(255, 255, 255, 0.05)'
												: 'rgba(0, 0, 0, 0.02)',
											borderRadius: 3,
											p: isSmallScreen ? 3 : 4,
											border: `1px solid ${theme.palette.divider}`,
											textAlign: 'left'
										}}
									>
										<Typography
											variant={isSmallScreen ? 'h3' : 'h2'}
											sx={{
												color: theme.palette.text.primary,
												lineHeight: 1.8,
												fontSize: isSmallScreen ? '1rem' : '1.1rem',
												fontStyle: 'italic',
												mb: 2
											}}
										>
											"The aim was to take a dying culture and bring it back to life. My creative mind
											is what keeps me motivated and diligent. I've never pushed to become well-known;
											instead, I've always worked to be truly consistent in whatever I do."
										</Typography>
										<Typography
											variant="body2"
											sx={{
												color: theme.palette.text.secondary,
												fontSize: '0.9rem',
												textAlign: 'center'
											}}
										>
											— Toufique Behardien, GQ South Africa
										</Typography>
									</Box>
								</Box>
							</Fade>
						</Container>
						{/* Footer */}
						<Footer />
						{/* GQ Article Dialog */}
						< Dialog
				open={articleDialogOpen}
				onClose={() => setArticleDialogOpen(false)}
				maxWidth="lg"
				fullWidth
				fullScreen={isSmallScreen}
				sx={{
					'& .MuiDialog-paper': {
						maxHeight: '100vh',
						height: '95vh',
						width: '100vw',
						backgroundImage: 'none',
						margin: { xs: 0, md: 'auto' },
						marginTop: { xs: 16, md: "auto" },
						borderTopLeftRadius: { xs: 12, md: 2 },
						borderTopRightRadius: { xs: 12, md: 2 },
						backgroundColor: theme.palette.common.white,
					},
					'& .MuiDialog-container': {
						padding: { xs: 0, md: 3 }
					}
				}}
			>
				<DialogTitle
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						backgroundColor: '#000000',
						color: '#FFFFFF',
						py: 2
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<img
							src="https://www.gq.com/verso/static/gq-global/assets/logo.svg"
							alt="GQ Logo"
							style={{
								height: '20px',
								filter: 'brightness(0) invert(1)'
							}}
						/>
						<Typography variant={isSmallScreen ? 'body1' : 'h6'} sx={{ color: '#FFFFFF', fontWeight: 'bold' }}>
							Toufique Behardien is a barber worth knowing
						</Typography>
					</Box>
					<IconButton
						onClick={() => setArticleDialogOpen(false)}
						sx={{ color: '#FFFFFF' }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ height: '100%', overflow: 'hidden', width: '100%' }}>
					<iframe
						src="https://www.gq.co.za/grooming/hair/toufique-behardien-is-a-barber-worth-knowing-60b1675d-40af-4011-8e79-a808d937d931"
						title="GQ Article - Toufique Behardien"
						style={{
							width: '100%',
							height: '100%',
							border: 'none',
							backgroundColor: theme.palette.common.white
						}}
					/>
				</DialogContent>
						</Dialog>
					</div>
				</Fade>
			</ScrollView>
		</>
	);
};
